'use client';

import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { BrainCircuit, Loader2 } from "lucide-react";
import { starkySenseService } from '@/lib/starky-sense-service';
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";
import { useTranslations } from 'next-intl';

// Utility function to dispatch Starky Sense toggle events
const dispatchStarkySenseToggle = (isEnabled: boolean) => {
  window.dispatchEvent(new CustomEvent('starky-sense-toggled', {
    detail: { isEnabled }
  }));
};

interface StarkySenseToggleButtonProps {
  disabled?: boolean;
  isTranslating?: boolean;
  onToggle?: (isStarkySenseActive: boolean) => void;
}

export function StarkySenseToggleButton({ disabled, isTranslating, onToggle }: StarkySenseToggleButtonProps) {
  const [isStarkySenseActive, setIsStarkySenseActive] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const t = useTranslations('starkySense');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await starkySenseService.getSettings();
        const starkySenseEnabled = settings?.isEnabled ?? true;
        setIsStarkySenseActive(starkySenseEnabled);
        onToggle?.(starkySenseEnabled);
        // Dispatch event to notify other components
        dispatchStarkySenseToggle(starkySenseEnabled);
      } catch (error) {
        console.error('Failed to load Starky Sense setting:', error);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, [onToggle]);

  const handleToggle = async () => {
    // Early returns for disabled states
    if (disabled || isLoadingSettings)
      return;

    // Prevent turning OFF Starky Sense during translation    
    if (isTranslating && isStarkySenseActive)
      return;

    const newStarkySenseState = !isStarkySenseActive;
    setIsStarkySenseActive(newStarkySenseState);
    onToggle?.(newStarkySenseState);

    // Immediately dispatch event for instant UI updates
    dispatchStarkySenseToggle(newStarkySenseState);

    try {
      await starkySenseService.updateSettings({ isEnabled: newStarkySenseState });
    } catch (error) {
      console.error('Failed to save Starky Sense setting:', error);
    }
  };

  // Calculate button disabled state 
  const isButtonDisabled = disabled || isLoadingSettings || (isTranslating && isStarkySenseActive);

  return (
    <TooltipWrapper
      disabled={isButtonDisabled}
      content={
        <p>
          {isTranslating && isStarkySenseActive ? t('cannotTurnOffWhileTranslating') :
            isStarkySenseActive ? t('turnOff') : t('turnOn')}
        </p>
      }
    >
      <Button
        variant="outline"
        size="icon"
        onClick={handleToggle}
        disabled={isButtonDisabled}
        className={isStarkySenseActive ? "bg-gradient-to-r hover:from-fuchsia-400 hover:to-blue-300 from-blue-400 to-fuchsia-500 text-white" : ""}
      >
        {isLoadingSettings ?
          <Loader2 className="h-4 w-4 animate-spin" />
          :
          <BrainCircuit className="h-4 w-4" />}
      </Button>
    </TooltipWrapper>
  );
}
